import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import config from '../../config';
import RHCABlogPost from './RHCABlogPost';
import CaregiverSupportBlogPost from './CaregiverSupportBlogPost';
import ABAEvolvedBlogPost from './ABAEvolvedBlogPost';
import ABAReimaginedBlogPost from './ABAReimaginedBlogPost';
import UDIDBlogPost from './UDIDBlogPost';
import SpeechNeurodiversityBlogPost from './SpeechNeurodiversityBlogPost';
import AACAwarenessBlogPost from './AACAwarenessBlogPost';

const STATIC_BLOG_IDS = [
  'rhca-blog-post',
  'caregiver-support-blog-post',
  'aba-evolved-blog-post',
  'aba-reimagined-blog-post',
  'udid-blog-post',
  'speech-neurodiversity-blog-post',
  'aac-awareness-blog-post'
];

function BlogsPagesDetails() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    if (STATIC_BLOG_IDS.includes(id)) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8-second timeout

    const fetchBlogData = async () => {
      try {
        // Fetch the current blog
        const blogResponse = await fetch(`${config.apiBaseUrl}/blogs/api/blogs/${id}`, { signal: controller.signal });
        const blogData = await blogResponse.json();
        
        if (blogData.success) {
          setBlog(blogData.data);
          
          // Fetch related blogs (all blogs except current one)
          const blogsResponse = await fetch(`${config.apiBaseUrl}/blogs/api/blogs`, { signal: controller.signal });
          const blogsData = await blogsResponse.json();
          
          if (blogsData.success) {
            setRelatedBlogs(blogsData.data.filter(b => b.id !== blogData.data.id));
          }
        } else {
          setError('Blog not found');
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          setError('Request timed out');
        } else {
          setError(err.message);
        }
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    fetchBlogData();
    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleString('default', { month: 'short' }),
      fullDate: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    };
  };

  if (id === 'rhca-blog-post') {
    return <RHCABlogPost />;
  }

  if (id === 'caregiver-support-blog-post') {
    return <CaregiverSupportBlogPost />;
  }

  if (id === 'aba-evolved-blog-post') {
    return <ABAEvolvedBlogPost />;
  }

  if (id === 'aba-reimagined-blog-post') {
    return <ABAReimaginedBlogPost />;
  }

  if (id === 'udid-blog-post') {
    return <UDIDBlogPost />;
  }

  if (id === 'speech-neurodiversity-blog-post') {
    return <SpeechNeurodiversityBlogPost />;
  }

  if (id === 'aac-awareness-blog-post') {
    return <AACAwarenessBlogPost />;
  }

  if (loading) {
    return <div className="container text-center py-5">Loading blog details...</div>;
  }

  if (error) {
    return <div className="container text-center py-5 text-danger">Error: {error}</div>;
  }

  if (!blog) {
    return <div className="container text-center py-5">Blog not found</div>;
  }

  const formattedDate = formatDate(blog.date);
  let parsedImages = [];

try {
  parsedImages =
    typeof blog.images === "string"
      ? JSON.parse(blog.images || "[]")
      : Array.isArray(blog.images)
      ? blog.images
      : [];
} catch (e) {
  console.error("Invalid images format:", blog.images);
  parsedImages = [];
}

  return (
    <div>
      <section className="page-header">
      <div 
  className="page-header-bg" 
  style={{ backgroundImage: `url(${config.baseUrl}/assets/images/backgrounds/blogpage-Header-1894-434.jpg)` }}
>
        </div>
        <div className="container">
          <div className="page-header__inner">
            <h2>Blog Details</h2>
            <ul className="thm-breadcrumb list-unstyled">
              <li><Link to="/">Home</Link></li>
              <li><span>/</span></li>
              <li><Link to="/blogs">Blogs</Link></li>
              <li><span>/</span></li>
              <li><Link to="">{blog.title}</Link></li>
            </ul>
          </div>
        </div>
      </section>
      
      <section className="blog-details">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8 col-lg-7">
              <div className="blog-details__left">
                <div className="blog-details__img">
                  <img 
                    src={`${config.apiBaseUrl}/uploads/${blog.main_image}`} 
                    alt={blog.title}
                    onError={(e) => {
                      e.target.src = 'assets/images/blog/blog-details-img-1.jpg';
                    }}
                  />
                  <div className="blog-details__date">
                    <p>{formattedDate.day}<br />{formattedDate.month}</p>
                  </div>
                </div>
                <div className="blog-details__content">
                  <ul className="blog-details__meta list-unstyled">
                    <li>
                      <Link to="#"><i className="fas fa-user-circle"></i>by {blog.username}</Link>
                    </li>
                    {/* You can add comments count if available in API */}
                  </ul>
                  <h3 className="blog-details__title">{blog.title}</h3>
                  <p className="blog-details__text-1">
                    {blog.description}
                  </p>
                  
                  {/* Additional images if available */}
                  {parsedImages.length > 0 && (
                    <div className="row mt-4">
                      {parsedImages.map((image, index) => (
                        <div key={index} className="col-md-4 mb-3">
                          <img 
                            src={`${config.apiBaseUrl}/uploads/${image}`} 
                            alt={`${blog.title} ${index + 1}`}
                            className="img-fluid"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="blog-details__bottom"></div>
                  
                  {/* Related blogs */}
                  {relatedBlogs.length > 0 && (
                    <div className="blog-details__pagenation">
                      {relatedBlogs.slice(0, 2).map((relatedBlog) => {
                        const relatedDate = formatDate(relatedBlog.date);
                        return (
                          <div key={relatedBlog.id} className="blog-details__pagenation-left">
                            <div className="blog-details__pagenation-left-img">
                              <img 
                                src={`${config.apiBaseUrl}/uploads/${relatedBlog.main_image}`} 
                                alt={relatedBlog.title}
                                onError={(e) => {
                                  e.target.src = 'assets/images/blog/blog-details-pagenation-img-1.jpg';
                                }}
                              />
                            </div>
                            <div className="blog-details__pagenation-left-content">
                              <p className="blog-details__pagenation-left-date">
                                <i className="fas fa-clock"></i>{relatedDate.fullDate}
                              </p>
                              <h4 className="blog-details__pagenation-left-title">
                                <Link to={`/blog-details/${relatedBlog.id}`}>{relatedBlog.title}</Link>
                              </h4>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BlogsPagesDetails;